/**
 * Pure cookie-policy helpers shared by the browser client, the edge middleware
 * and the auth callback. No Next.js / Supabase imports so they are unit-testable.
 *
 * Background (staging, 2026-08-19 16:55Z — the owner could not log in with SSO):
 *
 * 1. The middleware's "oversized cookie migration" measured the WHOLE cookie
 *    jar. A legacy `sb-neutralaiz-auth-token` cookie leaked from the parent
 *    domain (prod on engageiq.neutralaiz.com still writes the un-namespaced key)
 *    pushed the jar past the threshold, so the migration ran `setSession()` — a
 *    token refresh — on EVERY request. Concurrent requests raced the same
 *    refresh token, GoTrue's reuse detection revoked the family, and the user
 *    was left holding a dead cookie. The migration must only look at OUR OWN
 *    storage-key cookies: a foreign cookie is not ours to rewrite.
 *
 * 2. With a dead cookie, every request's `getUser()` refresh fails and
 *    gotrue-js `_removeSession()` deletes `<storageKey>-code-verifier` as a side
 *    effect. In the middleware that deletion was written straight into the
 *    request cookies, so `/auth/callback` exchanged the OAuth code with an empty
 *    verifier ("both auth code and code verifier should be non-empty") and SSO
 *    could never complete. The PKCE verifier belongs to an in-flight login; only
 *    the exchange may consume it. Dead-session cleanup must never delete it.
 */

export interface CookieLike {
  name: string;
  value: string;
}

/** The session cookie itself or one of its NUMERIC chunks (`<key>.0`, `<key>.1`, …).
 *  Strictly numeric — `storageKey.evil` must NOT count as ours, or a forged
 *  cookie could re-trip the oversized-cookie migration (Sol review 2026-08-19). */
export function isOwnAuthCookieName(name: string, storageKey: string): boolean {
  if (name === storageKey) return true;
  if (!name.startsWith(`${storageKey}.`)) return false;
  return /^\d+$/.test(name.slice(storageKey.length + 1));
}

/**
 * Own-session cookie names to clear from legacy Domain scopes when any exact
 * name occurs more than once. Browsers expose same-name cookies from different
 * scopes as separate entries, but omit the Domain attribute. A duplicate proves
 * an obsolete broader/host-only jar is shadowing the canonical environment jar.
 */
export function ownAuthCookieNamesForLegacyScopeCleanup(
  cookies: readonly CookieLike[],
  storageKey: string,
): string[] {
  const counts = new Map<string, number>();
  for (const cookie of cookies) {
    if (!isOwnAuthCookieName(cookie.name, storageKey)) continue;
    counts.set(cookie.name, (counts.get(cookie.name) ?? 0) + 1);
  }
  if (!Array.from(counts.values()).some((count) => count > 1)) return [];
  // Clear every observed own-session name from legacy scopes once a duplicate
  // proves canonical + legacy jars coexist. The two scopes may use different
  // chunk layouts, so clearing only the duplicated names can leave stale chunks.
  return Array.from(counts.keys());
}

/** Parent Domain scopes broader than the configured cookie domain. */
export function broaderCookieDomains(cookieDomain: string): string[] {
  const labels = cookieDomain.replace(/^\./, '').split('.').filter(Boolean);
  const domains: string[] = [];
  for (let index = 1; index < labels.length - 1; index += 1) {
    domains.push(`.${labels.slice(index).join('.')}`);
  }
  return domains;
}

/** Bytes occupied by OUR session cookie(s) only — foreign / legacy cookies excluded. */
export function ownAuthCookieBytes(cookies: readonly CookieLike[], storageKey: string): number {
  let total = 0;
  for (const cookie of cookies) {
    if (!isOwnAuthCookieName(cookie.name, storageKey)) continue;
    total += cookie.name.length + (cookie.value?.length ?? 0);
  }
  return total;
}

export function isCodeVerifierCookieName(name: string): boolean {
  return name.endsWith('-code-verifier');
}

/** Longest refresh token we will even LOOK at. Real GoTrue tokens are short
 *  (~12-64 chars); anything longer is a forged header, not a token, and must
 *  not be extracted, sent to GoTrue, or occupy dead-token memory. */
export const MAX_REFRESH_TOKEN_LENGTH = 512;

/** A `setAll` entry that would DELETE (empty) a PKCE code-verifier cookie. */
export function isCodeVerifierDeletion(cookie: CookieLike): boolean {
  return isCodeVerifierCookieName(cookie.name) && !cookie.value;
}

/**
 * Drop code-verifier deletions from a `setAll` batch. Non-empty verifier writes
 * (a login starting) and every other cookie pass through untouched.
 */
export function withoutCodeVerifierDeletions<T extends CookieLike>(cookies: readonly T[]): T[] {
  return cookies.filter((cookie) => !isCodeVerifierDeletion(cookie));
}

/**
 * Which browser-side keys (cookies AND localStorage) a dead-session cleanup may
 * clear. Supabase/sb- keys, except an in-flight login's PKCE verifier.
 */
export function isClearableAuthStorageKey(name: string): boolean {
  if (!(name.includes('supabase') || name.includes('sb-'))) return false;
  return !isCodeVerifierCookieName(name);
}

/**
 * Pull OUR session's refresh token out of the cookie jar (exact key or the
 * `<key>.0`, `<key>.1`… chunks, `base64-` tokens-only encoding or raw JSON).
 * Returns null when there is no readable session. Never logs the value.
 *
 * Used by the middleware's dead-token throttle: a refresh token GoTrue has
 * already declared dead must not be sent to GoTrue again on the next request
 * — it was being re-sent ~10×/s for as long as the browser kept polling.
 */
export function extractRefreshTokenFromCookies(
  cookies: readonly CookieLike[],
  storageKey: string,
): string | null {
  const exact = cookies.find((c) => c.name === storageKey)?.value;
  let raw: string | undefined = exact && exact.length > 0 ? exact : undefined;
  if (!raw) {
    const chunks = cookies
      .filter((c) => c.name.startsWith(`${storageKey}.`) && /^\d+$/.test(c.name.slice(storageKey.length + 1)))
      .sort((a, b) => Number(a.name.slice(storageKey.length + 1)) - Number(b.name.slice(storageKey.length + 1)));
    if (chunks.length === 0) return null;
    raw = chunks.map((c) => c.value).join('');
  }
  if (!raw) return null;
  // An over-length value is a forged/garbage cookie, not a real refresh token.
  // Returning it would (a) send it to GoTrue and (b) let it dodge the
  // dead-token throttle (which caps at MAX_REFRESH_TOKEN_LENGTH). Drop it.
  if (raw.length > MAX_REFRESH_TOKEN_LENGTH) return null;
  try {
    let json = raw;
    if (raw.startsWith('base64-')) {
      const b64 = raw.slice('base64-'.length).replace(/-/g, '+').replace(/_/g, '/');
      json = decodeBase64(b64);
    }
    const parsed = JSON.parse(json) as unknown;
    const token = Array.isArray(parsed)
      ? parsed[1]
      : (parsed as { refresh_token?: unknown } | null)?.refresh_token;
    return typeof token === 'string' && token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

function decodeBase64(b64: string): string {
  if (typeof atob === 'function') {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  }
  // Node < 16 fallback (tests / tooling)
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  return Buffer.from(b64, 'base64').toString('utf8');
}

/**
 * GoTrue's terminal refresh failures — the token is gone for good and every
 * retry will fail the same way. (A benign concurrent-refresh race is NOT one of
 * these: GoTrue re-issues the same session for the immediate parent token inside
 * its reuse interval instead of erroring.)
 */
export function isTerminalRefreshFailureMessage(message: string | null | undefined): boolean {
  const m = (message || '').toLowerCase();
  return (
    m.includes('already used') ||
    m.includes('invalid refresh token') ||
    m.includes('refresh_token_not_found') ||
    m.includes('refresh token not found')
  );
}

/** 64-bit FNV-1a as a 16-hex-char string. Non-cryptographic on purpose: keys
 *  the dead-token map with FIXED-SIZE digests so forged jumbo tokens cannot
 *  retain header-sized strings, while a 64-bit space makes an accidental
 *  collision (which would briefly lock out a VALID user) negligible at the
 *  map's 5000-entry bound. */
export function fnv1a64(input: string): string {
  // Exact FNV-1a-64 via BigInt (Sol review 2026-08-19: the split-32/32 version
  // mis-propagated the prime's carry — `fnv1a64("hello")` must be a430d84680aabd0b).
  const MASK = (1n << 64n) - 1n;
  let hash = 0xcbf29ce484222325n; // FNV-1a 64-bit offset basis
  const prime = 0x100000001b3n;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= BigInt(input.charCodeAt(i) & 0xff);
    hash = (hash * prime) & MASK;
  }
  return hash.toString(16).padStart(16, '0');
}

/** Bounded, TTL'd memory of refresh tokens GoTrue has declared dead.
 *  Stores 64-bit digests, never token material (Sol review 2026-08-19:
 *  forged jumbo tokens must not retain header-sized strings). */
export class DeadRefreshTokenMemory {
  private readonly entries = new Map<string, number>();

  constructor(
    private readonly ttlMs: number,
    private readonly maxEntries: number,
  ) {}

  private key(token: string): string | null {
    if (!token || token.length > MAX_REFRESH_TOKEN_LENGTH) return null;
    return fnv1a64(token);
  }

  isDead(token: string | null | undefined, now: number): boolean {
    const key = token ? this.key(token) : null;
    if (!key) return false;
    const expiresAt = this.entries.get(key);
    if (expiresAt === undefined) return false;
    if (expiresAt <= now) {
      this.entries.delete(key);
      return false;
    }
    return true;
  }

  markDead(token: string | null | undefined, now: number): void {
    const key = token ? this.key(token) : null;
    if (!key) return;
    if (!this.entries.has(key) && this.entries.size >= this.maxEntries) {
      const oldest = this.entries.keys().next().value;
      if (oldest !== undefined) this.entries.delete(oldest);
    }
    this.entries.set(key, now + this.ttlMs);
  }

  get size(): number {
    return this.entries.size;
  }
}
